import { ArrayUtils } from '@shopping/common'
import { CommonState } from 'common/types/commonState'
import { fetchStoreProductsByIds } from 'common/services/channel'
import { StoreProducts } from 'common/types/entities/store'
import { StateKey } from 'common/utils/flipState'
import { ProductParam } from 'common/types/params/index'
import { createCacheParam, isCacheHit } from 'common/utils/reduxCache'
import { setProductZzim } from 'common/services/zzim'
import { Error } from 'common/types/entities/error'
import HttpStatus from 'common/utils/httpStatus'
import { WindowProduct } from 'common/types/entities/mongo/product'
import { fetchProductsWithZzim } from 'common/services/list'

const initialState: StoreProducts = {
  hasMoreProducts: false,
  products: [],
  page: 0,
  itemCount: 0,
  storeId: null,
  productIds: [],
  newsId: null,
  cacheParam: null,
}

const FETCH = 'relatedProduct/FETCH'
const TOGGLE_CONTENT_RELATED_PRODUCTS_ZZIM = 'relatedProduct/TOGGLE_CONTENT_RELATED_PRODUCTS_ZZIM'

export const fetchRelatedProducts = (
  curState: StateKey,
  newsId: string,
  relationProductsParam: ProductParam,
  headers?: any,
) => async (dispatch, getState: () => CommonState) => {
  const cacheParam = createCacheParam(FETCH, 'fetchRelatedProducts', { curState, newsId, relationProductsParam })
  const contentRelatedProducts = getState().contentRelatedProducts[curState]
  if (isCacheHit(getState().contentRelatedProducts[curState], cacheParam)) {
    return
  }

  const relatedProducts = await fetchStoreProductsByIds(relationProductsParam)
  relatedProducts.products = await fetchProductsWithZzim(relatedProducts.products, headers)

  const products =
    relationProductsParam.page === 1
      ? relatedProducts.products
      : [...(contentRelatedProducts.products || []), ...relatedProducts.products]

  const state = {
    ...relationProductsParam,
    products,
    newsId,
    cacheParam,
    storeId: relationProductsParam.storeId ? relationProductsParam.storeId : null,
    hasMoreProducts: relatedProducts.hasMoreProducts,
  }

  dispatch({
    curState,
    state,
    type: FETCH,
  })
}

const handleError = (responseData: any) => {
  if (!responseData.bSuccess) {
    const error: Error = {
      status: responseData.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: responseData.htReturnValue.sMessage,
    }
    throw error
  }
}

const getZzimToggledProductItem = (product: WindowProduct) => {
  return {
    ...product,
    isMyZzim: !product.isMyZzim,
  }
}

export const toggleContentRelatedProductsZzim = (
  curState: StateKey,
  productId: string,
  zzimed: boolean,
) => async dispatch => {
  const responseData = await setProductZzim(productId, zzimed)
  handleError(responseData)

  dispatch({
    curState,
    productId,
    type: TOGGLE_CONTENT_RELATED_PRODUCTS_ZZIM,
  })
}

const findProductItemIndex = (products: WindowProduct[], productId: string) => {
  return products && products.findIndex(item => item._id === productId)
}

const toggleState = (targetIdx: number, items: any[]) => {
  if (ArrayUtils.isEmpty(items)) {
    return []
  }

  return [...items.slice(0, targetIdx), getZzimToggledProductItem(items[targetIdx]), ...items.slice(targetIdx + 1)]
}

const relatedProductReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH:
      return action.state
    case TOGGLE_CONTENT_RELATED_PRODUCTS_ZZIM:
      const targetItemIndex = findProductItemIndex(state.products, action.productId)
      return {
        ...state,
        products: toggleState(targetItemIndex, state.products),
      }
    default:
      return state
  }
}

export default relatedProductReducer
